import type { Task } from '@bigint-project/core';
import { rawNumber } from '@bigint-project/core';
import type { ColumnDef } from './types.js';
import { formatDuration } from './duration.js';
import { formatDateDisplay } from './date-format.js';

/** 預設欄位集合。 */
export const DEFAULT_COLUMNS: readonly ColumnDef[] = [
  { id: 'wbs', field: 'wbs', title: 'WBS', kind: 'text', width: 70, editable: false },
  { id: 'name', field: 'name', title: '任務名稱', kind: 'text', width: 240, editable: true },
  {
    id: 'durationMinutes',
    field: 'durationMinutes',
    title: '工期',
    kind: 'duration',
    width: 90,
    editable: true,
  },
  { id: 'start', field: 'start', title: '開始', kind: 'date', width: 120, editable: true },
  { id: 'finish', field: 'finish', title: '完成', kind: 'date', width: 120, editable: true },
  {
    // 'dependency-list' 欄位不對應 Task 的任何欄位或 customFieldValues,一律從
    // TaskGridProps.dependencies 反查(見 dependency-column.ts)。`field` 僅保留
    // 作為欄位識別的歷史命名,實際讀寫都繞過 getCellRawValue/formatCellValue。
    id: 'predecessors',
    field: 'predecessors',
    title: '前置任務',
    kind: 'dependency-list',
    width: 130,
    editable: true,
  },
  {
    // 'resource-list' 與 'dependency-list' 同理:指派資料在 Project 層級,不在 Task
    // 上,一律由 TaskGridProps.resourceNamesByTask 提供。唯讀 —— 指派要在下方的
    // 「資源指派」面板做,不是在格子裡打字。
    id: 'resourceNames',
    field: 'resourceNames',
    title: '資源',
    kind: 'resource-list',
    width: 160,
    editable: false,
  },
  {
    id: 'percentComplete',
    field: 'percentComplete',
    title: '完成百分比',
    kind: 'percent',
    width: 100,
    editable: true,
  },
  { id: 'fixedCost', field: 'fixedCost', title: '成本', kind: 'currency', width: 110, editable: true },
  {
    // 'expected-percent' / 'progress-status' 與 'resource-list' 同理:值來自
    // 引擎推導(computeExpectedProgress),不是 Task 欄位,由 expectedProgressByTask 提供。
    id: 'expectedPercent',
    field: 'expectedPercent',
    title: '預期進度',
    kind: 'expected-percent',
    width: 90,
    editable: false,
  },
  {
    id: 'progressStatus',
    field: 'progressStatus',
    title: '進度狀態',
    kind: 'progress-status',
    width: 90,
    editable: false,
  },
  {
    id: 'totalFloat',
    field: 'totalFloat',
    title: '總浮時',
    kind: 'duration',
    width: 90,
    editable: false,
  },
];

/** 取得儲存格顯示用的原始值(未格式化)。 */
export function getCellRawValue(task: Task, column: ColumnDef): unknown {
  if (column.custom) {
    const fieldId = column.customFieldId ?? column.field;
    const values = task.customFieldValues;
    // 只有任務根本沒有這個 key 時才顯示 definition default；key 明確存在且為
    // null 時是使用者清空後的值，不能以預設值蓋回去。
    return values && Object.prototype.hasOwnProperty.call(values, fieldId)
      ? values[fieldId]
      : column.customFieldDefaultValue ?? null;
  }
  return (task as unknown as Record<string, unknown>)[column.field];
}

/** 將儲存格值格式化為顯示字串。 */
export function formatCellValue(task: Task, column: ColumnDef): string {
  const value = getCellRawValue(task, column);
  if (value === null || value === undefined) return '';

  if (column.custom) {
    const lookupItem = column.customFieldLookup?.find((item) => Object.is(item.value, value));
    if (lookupItem) return lookupItem.label;
    if (column.customFieldKind === 'Date') return formatDateDisplay(value);
    return String(value);
  }

  switch (column.kind) {
    case 'duration':
      return formatDuration(value as Task['durationMinutes']);
    case 'percent':
      return `${rawNumber(value as Task['percentComplete'])}%`;
    case 'currency':
      return (rawNumber(value as Task['fixedCost'])).toLocaleString('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        maximumFractionDigits: 0,
      });
    case 'date':
      // 顯示格式固定 yyyy-mm-dd(見 date-format.ts),不隨 locale 變動。
      return formatDateDisplay(value);
    case 'resource-list':
      // 同 'dependency-list':資料不在 Task 上,呼叫端改讀 resourceNamesByTask。
      return '';
    case 'expected-percent':
    case 'progress-status':
      // 同 'resource-list':值由 expectedProgressByTask 提供,Task 本身沒有這些欄位。
      return '';
    case 'dependency-list':
      // 'dependency-list' 欄位的顯示值必須來自 dependencies + tasks(見
      // dependency-column.ts 的 formatTaskPredecessorsDisplay),Task 本身不持有
      // 依賴資料。TaskGrid 會在渲染/複製時繞過本函式改呼叫該 helper;這裡回傳
      // 空字串只是防呆保底,不應被實際路徑觸發。
      return '';
    case 'number':
    case 'text':
    default:
      return String(value);
  }
}
